import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Building2,
  Trash2,
  Calendar,
  User,
  Activity,
  Loader2,
  Database,
  ShieldCheck,
  CheckCircle2,
  CreditCard,
  MapPin,
  Hash
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import styles from '../../../styles/MasterView.module.css';
import api from '../../../api/axios';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import BankDrawer from './BankDrawer';
import NotFound from '../../Error/NotFound';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ViewBank = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bank, setBank] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchBank = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/bank-accounts/${id}`);
      if (response.data.success) {
        setBank(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching bank details:', error);
      toast.error('Failed to load bank details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBank();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      title: 'Delete Bank?',
      text: `Are you sure you want to delete ${bank.bank_name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/bank-accounts/${bank.id}`);
          if (response.data.success) {
            toast.success('Bank deleted successfully');
            navigate('/banks');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Error deleting bank');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className={styles['view-master-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
             <Loader2 size={40} className={styles['spinner']} />
             <p>Fetching Banking Profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!bank) {
    return <NotFound />;
  }


  return (
    <div className={`${styles['view-master-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Bank Account Details</h1>
              <p>View corporate banking configurations and transaction summaries</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/banks')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Update Bank</span>
              </button>
              <button className={styles['btn-secondary']} onClick={handleDelete} style={{ color: '#ef4444' }}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>

          {/* Master Profile Header Card */}
          <div className={styles['master-profile-header']}>
            <div className={styles['header-main']}>
              <div 
                className={styles['avatar-large']}
                style={{ background: 'var(--primary-gradient)' }}
              >
                <Building2 size={32} />
              </div>
              <div className={styles['profile-basic-info']}>
                <div className={styles['title-flex']}>
                  <h1>{bank.bank_name}</h1>
                  <span className={styles['status-badge']}>
                    <ShieldCheck size={14} /> VERIFIED ACCOUNT
                  </span>
                </div>
                <div className={styles['master-meta']}>
                  <div className={styles['meta-item']}>
                    <Hash size={16} />
                    <span>A/C: <strong>{bank.account_number}</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Calendar size={16} />
                    <span>Linked: <strong>{new Date(bank.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-blue']}`}><Activity size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Recent Transactions</label>
                <h2>24</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-green']}`}><CheckCircle2 size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Settled Payments</label>
                <h2>₹12.4L</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-orange']}`}><Database size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Linked Vendors</label>
                <h2>18</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-slate']}`}><ShieldCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Security Status</label>
                <h2>Encrypted</h2>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* Primary Details */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><CreditCard size={20} /></div>
                  <h3>Account Configuration</h3>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Official Institution</span>
                      <span className={styles['d-value']}>{bank.bank_name}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>IFSC Code</span>
                      <span className={styles['d-value']}>{bank.ifsc_code}</span>
                    </div>
                  </div>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Account Number</span>
                      <span className={styles['d-value']}>{bank.account_number}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Branch Name</span>
                      <span className={styles['d-value']}>{bank.branch}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transaction Logs */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Activity size={20} /></div>
                  <h3>Recent Transaction Index</h3>
                </div>
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                  <p style={{ fontSize: '13px' }}>Transaction history and settlement logs will be visible once synchronized with the Finance module.</p>
                </div>
              </div>
            </div>

            <div className={styles['right-column']}>
              {/* Registry Info */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><ShieldCheck size={20} /></div>
                  <h3>Registry Metadata</h3>
                </div>
                <div className={styles['info-block']}>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><User size={18} /></div>
                    <div className={styles['sidebar-text']}>
                       <label>ONBOARDED BY</label>
                       <span>{bank.creator?.name || 'Finance Dept'}</span>
                    </div>
                  </div>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><Calendar size={18} /></div>
                    <div className={styles['sidebar-text']}>
                       <label>CREATED ON</label>
                       <span>{new Date(bank.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <BankDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        bank={bank}
        onSave={fetchBank}
      />
    </div>
  );
};

export default ViewBank;
